// Paper sizes for the map print, and the two rules that depend on the chosen
// size: what goes into `@page { size: … }`, and how hard to re-render the map so
// the sheet is sharp.
//
// Both used to be constants tuned for A4. Neither survives contact with A0.

// 'custom' deliberately has no row in PAPER_FORMATS below: a custom sheet
// carries its millimetres directly (PrintSheetSpec.page), so nothing ever
// needs to look 'custom' up in the paper table. paperSize()'s fallback to
// PAPER_FORMATS[0] keeps pageMm('custom', …) safe rather than throwing, for
// any caller that reaches it before `page` is threaded through.
export type PaperFormat = 'a4' | 'a3' | 'a2' | 'a1' | 'a0' | 'custom'
export type PrintOrientation = 'portrait' | 'landscape'

export interface PaperSize {
  id: PaperFormat
  label: string
  /** Short edge in millimetres (portrait width). */
  w: number
  /** Long edge in millimetres (portrait height). */
  h: number
}

/**
 * Offered largest-last, matching the legacy app's A4→A0 run. Letter was offered
 * briefly and removed: it is not used in the market areas this app serves, and
 * its odd width was the only thing that made the format row wrap.
 */
export const PAPER_FORMATS: PaperSize[] = [
  { id: 'a4', label: 'A4', w: 210, h: 297 },
  { id: 'a3', label: 'A3', w: 297, h: 420 },
  { id: 'a2', label: 'A2', w: 420, h: 594 },
  { id: 'a1', label: 'A1', w: 594, h: 841 },
  { id: 'a0', label: 'A0', w: 841, h: 1189 },
]

export function paperSize(format: PaperFormat): PaperSize {
  // An unknown id means a caller invented a format; printing something is better
  // than throwing inside a print handler, and A4 is the safe default sheet.
  return PAPER_FORMATS.find((p) => p.id === format) ?? PAPER_FORMATS[0]
}

/** Page dimensions in mm, oriented — width first. */
export function pageMm(
  format: PaperFormat,
  orientation: PrintOrientation,
): { w: number; h: number } {
  const p = paperSize(format)
  return orientation === 'landscape' ? { w: p.h, h: p.w } : { w: p.w, h: p.h }
}

/**
 * What a rasterised map should resolve to on paper.
 *
 * 300 DPI is the figure the browser print pass was tuned for at A4, and it stays
 * the target for the server-rendered sheet: a map read at arm's length wants it.
 * Large sheets are viewed from further away and would look fine at less, but
 * nothing gains from being cheaper here.
 *
 * The only reader left is the border law (`groundStrokeMm`), which needs a DPI to
 * turn a stroke's paper millimetres into the pixels the sheet is composed in.
 */
export const TARGET_PRINT_DPI = 300
